import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Modal from 'LuxKit/Modal/Modal'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { EmptyArray } from 'lib/array/arrayUtils'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalSearchTileFeaturesList, { MAX_INCLUSIONS_TO_SHOW } from '../shared/ExperimentalSearchTileFeaturesList'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useBedbankRates from 'hooks/useBedbankRates'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankCancellationPolicy from '../../../OfferPage/Bedbank/BedbankCancellationPolicy'
import useOffer from 'hooks/Offers/useOffer'
import ExperimentalHotelUpgradableInclusionsLabel from './ExperimentalHotelUpgradableInclusionsLabel'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LuxPlusInclusions from '../../../../../luxPlus/components/LuxPlusInclusions'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
}

function ExperimentalHotelSearchTileFeatures({
  bestPackage,
  filters,
  offer,
  offerMetaData,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const isBedbankRate = metaData?.pricing.rateType === 'bedbank'

  const [{ hotelOnlyRates: bedbankRate }, isFetchingBedbankRates] = useBedbankRates(
    isBedbankRate ? metaData?.pricing.ratePropertyId : undefined,
    filters?.rooms,
    filters?.checkIn,
    filters?.checkOut,
    isBedbankRate,
  )

  const [bedbankOffer] = useOffer<App.BedbankOffer>(isBedbankRate ? metaData?.pricing.ratePropertyId : undefined)

  const bedbankRateForRoom = useMemo(() => {
    if (!isBedbankRate) return undefined
    const roomRateId = metaData?.pricing.lowestPriceRoomRateId
    if (!roomRateId || !bedbankRate.length) return undefined

    return bedbankRate.flatMap(rates => rates).find(rate => rate.id === roomRateId)
  }, [isBedbankRate, metaData?.pricing.lowestPriceRoomRateId, bedbankRate])

  const bedbankInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!bedbankRateForRoom?.inclusions?.length) return EmptyArray
    return bedbankRateForRoom.inclusions.filter(inclusion => inclusion.description !== 'Room only')
  }, [bedbankRateForRoom?.inclusions])

  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)

  const luxPlusInclusionsThatCanBeDisplayed = useMemo<Array<App.OfferInclusion>>(() => {
    return isLuxPlusMember ? luxPlusInclusions : EmptyArray
  }, [isLuxPlusMember, luxPlusInclusions])

  const featuresToDisplay = useMemo<{
    featureList: Array<App.OfferInclusion>
    featureType: 'inclusions' | 'amenities'
  }>(() => {
    if (bedbankInclusions.length > 0) {
      return { featureList: bedbankInclusions, featureType: 'inclusions' }
    }

    if (bonusInclusions.length > 0 || inclusions.length > 0) {
      return {
        featureList: [...inclusions, ...bonusInclusions],
        featureType: 'inclusions',
      }
    }

    const amenities = offer.lowestPricePackage?.roomType.amenities ?? []
    if (amenities.length > 0) return { featureList: amenities, featureType: 'amenities' }

    return { featureList: EmptyArray, featureType: 'inclusions' }
  }, [bedbankInclusions, bonusInclusions, inclusions, offer.lowestPricePackage?.roomType.amenities])

  const loyaltyCount = useLoyaltyBenefitInclusions(offer).length
  const showLoyaltyInline = featuresToDisplay.featureType === 'amenities' ||
    featuresToDisplay.featureList.length + loyaltyCount <= MAX_INCLUSIONS_TO_SHOW

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionText={intl.formatMessage({
        id: '5n7AcIxL6sr_rLtKdVWM',
        defaultMessage: 'View offer',
        description: 'CTA button in experimental hotel inclusions modal to view the offer',
      })}
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <OfferInclusionsList inclusions={featuresToDisplay.featureList} />
          <LoyaltyInclusions offer={offer} variant="offer-page" />
        </Group>
      </Group>
    </Modal>)
  }, [offer, featuresToDisplay, offerUrl, showModal, intl])

  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type

  const upgradableInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!offer.packages.length) return EmptyArray

    // Find the package with the most inclusions
    const packageWithMostInclusions = offer.packages.reduce((max, pkg) => {
      const maxCount = (max.inclusions?.length ?? 0) + (max.bonusInclusions?.length ?? 0)
      const pkgCount = (pkg.inclusions?.length ?? 0) + (pkg.bonusInclusions?.length ?? 0)
      return pkgCount > maxCount ? pkg : max
    }, offer.packages[0])

    const mostInclusions = [
      ...(packageWithMostInclusions.inclusions ?? []),
      ...(packageWithMostInclusions.bonusInclusions ?? []),
    ]
    if (!mostInclusions.length) return EmptyArray

    const inclusionsToDisplay = featuresToDisplay.featureType === 'inclusions' ? featuresToDisplay.featureList : []
    // Filter out inclusions that are already displayed or duplicated
    const displayedDescriptions = new Set(inclusionsToDisplay.map(i => i.description))
    const seenDescriptions = new Set<string>()

    const upgradableInclusions = mostInclusions.filter(inclusion => {
      const desc = inclusion.description
      if (displayedDescriptions.has(desc) || seenDescriptions.has(desc)) return false
      seenDescriptions.add(desc)
      return true
    })

    if (
      upgradableInclusions.length === 1 &&
      upgradableInclusions[0].description.toLowerCase().includes('breakfast')
    ) {
      return EmptyArray
    }

    return upgradableInclusions.length ? upgradableInclusions : EmptyArray
  }, [offer.packages, featuresToDisplay])

  const saveAmount = !!metaData?.pricing?.lowestMemberPrice && !!metaData?.pricing?.lowestPrice ?
    metaData?.pricing?.lowestPrice - metaData?.pricing?.lowestMemberPrice : undefined

  return (
    <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" verticalAlign="space-between" grow gap={8} noDisplayWhenEmpty>
      <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" gap={8} noDisplayWhenEmpty>
        {isBedbankRate && isFetchingBedbankRates && (
          <VerticalSpacer gap={4}>
            <TextLoadingBox typography="body-small" width="60%" />
            <TextLoadingBox typography="body-small" width="80%" />
            <TextLoadingBox typography="body-small" width="40%" />
          </VerticalSpacer>
        )}
        {!!luxPlusInclusionsThatCanBeDisplayed?.length && <LuxPlusInclusions
          luxPlusInclusions={luxPlusInclusionsThatCanBeDisplayed}
          accommodationOffer={offer}
          hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
          showMinStayDescription
          onShowMoreClick={openAllInclusionsModal}
        />}
        {!(isBedbankRate && isFetchingBedbankRates) && (
          <>
            <Group direction="vertical" gap={4}>
              <ExperimentalSearchTileFeaturesList
                features={featuresToDisplay.featureList}
                featuresType={featuresToDisplay.featureType}
                additionalHiddenCount={showLoyaltyInline ? 0 : loyaltyCount}
                cancellationPolicy={
                  <>
                    {!!cancellationPolicyType && !isBedbankRate && (
                      <TileCancellationPolicy
                        icon={<LineCalendarIcon />}
                        cancellationPolicyType={cancellationPolicyType}
                        checkInDate={filters?.checkIn}
                        timezoneOffset={offer.property.timezoneOffset}
                        offerType={isBedbankRate ? 'bedbank_hotel' : offer.type}
                      />
                    )}
                    {isBedbankRate && bedbankOffer && bedbankRateForRoom && (
                      <BedbankCancellationPolicy
                        rate={bedbankRateForRoom}
                        showNonCancellation={false}
                        icon={<LineCalendarIcon />}
                      />
                    )}
                  </>
                }
                onMoreClick={openAllInclusionsModal}
              />
              {showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="search-tile" />}
            </Group>

            {!!luxPlusInclusions?.length && !isLuxPlusMember && (
              <LuxPlusLabelWithModal
                type="member-inclusions"
                inclusions={luxPlusInclusions}
                offer={offer}
                hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
              />
            )}
            {!!saveAmount && saveAmount > 0 && !luxPlusInclusions?.length && (
              <LuxPlusLabelWithModal
                type="member-price-offer"
                offer={offer}
                saveAmount={saveAmount}
              />
            )}
            {!!upgradableInclusions?.length && <ExperimentalHotelUpgradableInclusionsLabel offer={offer} inclusions={upgradableInclusions} filters={filters} />}
          </>
        )}
      </Group>
      <SearchTileRoomBreakdown offer={offer} filters={filters} />
    </Group>
  )
}

export default ExperimentalHotelSearchTileFeatures
